/**
 * React component for building actions.
 * Displays building type selection buttons for Builder units.
 * Shows hierarchical menu: unit actions + build button -> building types -> tower types
 */

import { useBuildModeStore } from '@shared/stores/buildModeStore';
import {
  BuildingType,
  getBuildingDefinition,
  getBuildingCost,
  isTowerType,
} from '@shared/constants/buildings';

interface BuildingActionsProps {
  scene: unknown | null;
}

export default function BuildingActions({ scene }: BuildingActionsProps) {
  const {
    isBuildModeActive,
    selectedBuildingType,
    isBuildMenuOpen,
    isTowerSubmenuOpen,
    activateBuildMode,
    deactivateBuildMode,
    closeBuildMenu,
    toggleTowerSubmenu,
  } = useBuildModeStore();

  // Check if tech building is built (for Magic Tower requirement)
  const isTechBuildingBuilt = (): boolean => {
    if (!scene || typeof scene !== 'object' || scene === null) return false;
    const sceneWithMethods = scene as {
      getBuildingUpgradeSystem?: () => {
        isTechBuildingBuilt: () => boolean;
      } | null;
    };
    const upgradeSystem = sceneWithMethods.getBuildingUpgradeSystem?.();
    return upgradeSystem?.isTechBuildingBuilt() ?? false;
  };

  const handleBuildingTypeClick = (buildingType: BuildingType) => {
    if (isBuildModeActive && selectedBuildingType === buildingType) {
      deactivateBuildMode();
    } else {
      activateBuildMode(buildingType);
    }
  };

  const handleTowerButtonClick = () => {
    toggleTowerSubmenu();
  };

  const isTowerSelected = selectedBuildingType ? isTowerType(selectedBuildingType) : false;

  // Don't render anything if build menu is not open and no build mode active
  // The build menu button should be shown in UnitActionsMenu instead
  if (!isBuildMenuOpen && !isBuildModeActive) {
    return null;
  }

  // Main build menu - show building types
  if (isBuildMenuOpen && !isTowerSubmenuOpen) {
    return (
      <div className="flex flex-wrap gap-2">
        <BuildingButton
          hotkey="T"
          onClick={handleTowerButtonClick}
          tooltip="Tower (T) → Archer (A), Cannon (C), Magic (G)"
          active={isTowerSelected}
        />
        <BuildingButton
          hotkey="W"
          onClick={() => handleBuildingTypeClick(BuildingType.WALL)}
          tooltip={`${getBuildingDefinition(BuildingType.WALL).name} (W) - ${getBuildingCost(BuildingType.WALL)}g`}
          active={isBuildModeActive && selectedBuildingType === BuildingType.WALL}
        />
        <BuildingButton
          hotkey="B"
          onClick={() => handleBuildingTypeClick(BuildingType.BARRACKS)}
          tooltip={`${getBuildingDefinition(BuildingType.BARRACKS).name} (B) - ${getBuildingCost(BuildingType.BARRACKS)}g`}
          active={isBuildModeActive && selectedBuildingType === BuildingType.BARRACKS}
        />
        <BuildingButton
          hotkey="H"
          onClick={() => handleBuildingTypeClick(BuildingType.HERO_SPAWNER)}
          tooltip={`${getBuildingDefinition(BuildingType.HERO_SPAWNER).name} (H) - ${getBuildingCost(BuildingType.HERO_SPAWNER)}g`}
          active={isBuildModeActive && selectedBuildingType === BuildingType.HERO_SPAWNER}
        />
        <BuildingButton
          hotkey="E"
          onClick={() => handleBuildingTypeClick(BuildingType.TECH_BUILDING)}
          tooltip={`${getBuildingDefinition(BuildingType.TECH_BUILDING).name} (E) - ${getBuildingCost(BuildingType.TECH_BUILDING)}g`}
          active={isBuildModeActive && selectedBuildingType === BuildingType.TECH_BUILDING}
        />
        <BuildingButton
          hotkey="M"
          onClick={() => handleBuildingTypeClick(BuildingType.MINE)}
          tooltip={`${getBuildingDefinition(BuildingType.MINE).name} (M) - ${getBuildingCost(BuildingType.MINE)}g - Must be placed on mine node`}
          active={isBuildModeActive && selectedBuildingType === BuildingType.MINE}
        />
        <BuildingButton
          hotkey="Esc"
          onClick={closeBuildMenu}
          tooltip="Close build menu (B or Escape)"
        />
      </div>
    );
  }

  // Tower submenu - show only tower types
  if (isBuildMenuOpen && isTowerSubmenuOpen) {
    const magicTowerDisabled = !isTechBuildingBuilt();
    return (
      <div className="flex flex-wrap gap-2">
        <BuildingButton
          hotkey="A"
          onClick={() => handleBuildingTypeClick(BuildingType.TOWER_ARCHER)}
          tooltip={`${getBuildingDefinition(BuildingType.TOWER_ARCHER).name} (A) - ${getBuildingCost(BuildingType.TOWER_ARCHER)}g`}
          active={isBuildModeActive && selectedBuildingType === BuildingType.TOWER_ARCHER}
        />
        <BuildingButton
          hotkey="C"
          onClick={() => handleBuildingTypeClick(BuildingType.TOWER_CANNON)}
          tooltip={`${getBuildingDefinition(BuildingType.TOWER_CANNON).name} (C) - ${getBuildingCost(BuildingType.TOWER_CANNON)}g`}
          active={isBuildModeActive && selectedBuildingType === BuildingType.TOWER_CANNON}
        />
        <BuildingButton
          hotkey="G"
          onClick={() => !magicTowerDisabled && handleBuildingTypeClick(BuildingType.TOWER_MAGIC)}
          tooltip={
            magicTowerDisabled
              ? `${getBuildingDefinition(BuildingType.TOWER_MAGIC).name} (G) - Requires Tech Building`
              : `${getBuildingDefinition(BuildingType.TOWER_MAGIC).name} (G) - ${getBuildingCost(BuildingType.TOWER_MAGIC)}g`
          }
          active={isBuildModeActive && selectedBuildingType === BuildingType.TOWER_MAGIC}
          disabled={magicTowerDisabled}
        />
        <BuildingButton
          hotkey="Esc"
          onClick={closeBuildMenu}
          tooltip="Close build menu (B or Escape)"
        />
      </div>
    );
  }

  return null;
}

/**
 * Square building button component that shows only the hotkey.
 */
function BuildingButton({
  hotkey,
  onClick,
  tooltip,
  active = false,
  disabled = false,
}: {
  hotkey: string;
  onClick: () => void;
  tooltip: string;
  active?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        w-12 h-12 flex items-center justify-center
        rounded border-2 font-mono text-sm font-bold
        transition-all
        ${
          disabled
            ? 'bg-gray-700 border-gray-600 text-gray-500 cursor-not-allowed opacity-50'
            : active
              ? 'bg-blue-600 border-blue-400 text-white'
              : 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600 hover:border-gray-500'
        }
      `}
      title={tooltip}
    >
      {hotkey}
    </button>
  );
}
